import { useLayoutEffect, useMemo, useRef } from 'react';
import { onlineManager, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { AuthUser } from '@stayon/auth-core';
import { toApiError, type ConversationDetail } from '@/shared/api';
import { createAuthStore, identityKey, useAuth } from '@/shared/auth';

type ResolutionCommand = {
  id: string;
  resolved: boolean;
  expectedConversationRevision: number;
  expectedLastLiveGuestMessageId: string | null;
};
type SeenThread = Pick<
  ConversationDetail,
  'id' | 'isLocallyResolved' | 'conversationRevision' | 'lastLiveGuestMessageId'
>;
const scopeIdentity = (auth: { user: AuthUser | null; status: string }) =>
  [
    identityKey(auth.user),
    auth.user?.homeTenantId,
    auth.user?.sessionFamilyId,
    auth.user?.roles.slice().sort().join(','),
    auth.user?.privilegeCatalogVersion,
    auth.user?.privileges?.slice().sort().join(','),
    auth.status === 'unauthenticated',
  ].join('|');

export function useLocalConversationResolution(targetId: string | null) {
  const auth = useAuth();
  const identity = scopeIdentity(createAuthStore().getState());
  const binding = useMemo(
    () => ({ api: auth.api, identity, targetId }),
    [auth.api, identity, targetId],
  );
  const active = useRef<typeof binding | null>(binding);
  const inFlight = useRef<typeof binding | null>(null);
  useLayoutEffect(() => {
    active.current = binding;
    return () => {
      active.current = null;
    };
  }, [binding]);
  const client = useQueryClient();
  const { t } = useTranslation('conversationResolution');
  const sameScope = (scope: typeof binding) => {
    const live = createAuthStore().getState();
    return live.api === scope.api && scopeIdentity(live) === scope.identity;
  };
  const selected = (scope: typeof binding) => active.current === scope && sameScope(scope);
  const mutation = useMutation({
    mutationFn: async (command: ResolutionCommand): Promise<void> => {
      const scope = binding;
      if (!onlineManager.isOnline() || !selected(scope) || scope.targetId !== command.id) {
        if (inFlight.current === scope) inFlight.current = null;
        return;
      }
      try {
        const thread = await scope.api.messaging.setLocalResolution(command.id, {
          resolved: command.resolved,
          expectedConversationRevision: command.expectedConversationRevision,
          expectedLastLiveGuestMessageId: command.expectedLastLiveGuestMessageId,
        });
        if (selected(scope)) {
          const queryKey = ['inbox', 'thread', command.id];
          await client.cancelQueries({ queryKey, exact: true });
          if (!selected(scope)) return;
          client.setQueryData(queryKey, thread);
          toast.success(t(thread.isLocallyResolved ? 'resolvedSuccess' : 'undoSuccess'));
        }
      } catch (error) {
        if (!selected(scope)) return;
        const problem = await toApiError(error);
        if (!selected(scope)) return;
        toast.error(
          [0, 502, 503, 504].includes(problem.status)
            ? t('unknown')
            : problem.status === 409
              ? t('changed')
              : t('failed'),
        );
      } finally {
        if (inFlight.current === scope) inFlight.current = null;
        if (sameScope(scope))
          await Promise.all([
            client.invalidateQueries({ queryKey: ['inbox'] }),
            client.invalidateQueries({ queryKey: ['operations'] }),
          ]);
      }
    },
    networkMode: 'always',
    retry: false,
    gcTime: 0,
  });
  const reset = mutation.reset;
  useLayoutEffect(() => {
    reset();
  }, [binding, reset]);
  const run = (thread: SeenThread) => {
    if (
      !onlineManager.isOnline() ||
      !selected(binding) ||
      binding.targetId !== thread.id ||
      inFlight.current === binding
    )
      return;
    inFlight.current = binding;
    mutation.mutate({
      id: thread.id,
      resolved: !thread.isLocallyResolved,
      expectedConversationRevision: thread.conversationRevision,
      expectedLastLiveGuestMessageId: thread.lastLiveGuestMessageId,
    });
  };
  return { run, pending: mutation.isPending };
}
